// common/services/player/familyScope.ts
//
// Family Management is reachable from two surfaces that hit DIFFERENT backend
// routes for the same four operations:
//
//   'me'     → the profile screen. The player is one of the current user's own
//              linked players, so the self-scoped /users/me/players/:id/family
//              routes apply.
//   'roster' → the team roster. The player belongs to someone else; access is
//              granted by the caller's team-admin permission, so the
//              roster-scoped /teams/:team/rosters/:roster/players/:id/family
//              routes apply instead.
//
// The scope is threaded from the entry point down to the services so neither
// surface has to fork the family components. Omitting it everywhere yields
// 'me', which is exactly the pre-existing behaviour.
//
// Resend is deliberately absent: it posts to /invitations/:contactId/resend,
// which is identified by the invitation alone and is the same on both surfaces.
import { ENDPOINTS } from '../../api/endpoints'

export type FamilyScope =
  | { kind: 'me' }
  | { kind: 'roster'; teamId: string; rosterId: string }

export const ME_FAMILY_SCOPE: FamilyScope = { kind: 'me' }

/** GET the player's family list. */
export const familyListPath = (playerId: string, scope?: FamilyScope): string =>
  scope?.kind === 'roster'
    ? ENDPOINTS.TEAMS.GET_ROSTER_PLAYER_FAMILY(
        scope.teamId,
        scope.rosterId,
        playerId
      )
    : ENDPOINTS.PLAYERS.GET_PLAYER_FAMILY(playerId)

/** POST a new family-contact invitation. */
export const familyInvitePath = (
  playerId: string,
  scope?: FamilyScope
): string =>
  scope?.kind === 'roster'
    ? ENDPOINTS.TEAMS.INVITE_ROSTER_PLAYER_FAMILY(
        scope.teamId,
        scope.rosterId,
        playerId
      )
    : ENDPOINTS.PLAYERS.INVITE_PLAYER_FAMILY(playerId)

/** PUT an existing family contact. */
export const familyContactUpdatePath = (
  playerId: string,
  contactId: string,
  scope?: FamilyScope
): string =>
  scope?.kind === 'roster'
    ? ENDPOINTS.TEAMS.UPDATE_ROSTER_PLAYER_FAMILY_CONTACT(
        scope.teamId,
        scope.rosterId,
        playerId,
        contactId
      )
    : ENDPOINTS.PLAYERS.UPDATE_PLAYER_FAMILY_CONTACT(playerId, contactId)

/** DELETE a family contact. */
export const familyContactDeletePath = (
  playerId: string,
  contactId: string,
  scope?: FamilyScope
): string =>
  scope?.kind === 'roster'
    ? ENDPOINTS.TEAMS.DELETE_ROSTER_PLAYER_FAMILY_CONTACT(
        scope.teamId,
        scope.rosterId,
        playerId,
        contactId
      )
    : ENDPOINTS.PLAYERS.DELETE_PLAYER_FAMILY_CONTACT(playerId, contactId)

/**
 * Cache discriminator for a scope. The two surfaces return the same player's
 * family from different routes, so their react-query entries must not collide —
 * otherwise a roster read would serve the profile screen's cached list and a
 * mutation would invalidate the wrong one.
 */
export const familyScopeKey = (scope?: FamilyScope): string =>
  scope?.kind === 'roster' ? `roster:${scope.teamId}:${scope.rosterId}` : 'me'
